import { Grid, Tabs, Tab, Box } from '@mui/material'
import * as React from 'react'

import { useBookingForm } from '../../hooks'
import { generateDateRange } from '../../utils/dateRangeHelpers'
import { BookingForm, DeskAvailability, TeamMemberSelector } from '../booking'
import DialogAlert from '../DialogAlert'

import OfficeDrawingViewer from './OfficeDrawingViewer'

/**
 * OfficeDetail - Container component for desk booking functionality
 *
 * Orchestrates the booking process by combining form input, desk selection,
 * and calendar display. Uses the useBookingForm hook for state management.
 */
export default function OfficeDetail() {
  const [activeTab, setActiveTab] = React.useState(0)

  const {
    userState,
    busy,
    selectedDesks,
    notifyUser,
    confirmOpen,
    teamMembers,
    selectedBookingOwnerId,
    handleOfficeChange,
    handleUnitChange,
    handleFromDayChange,
    setNewAmountValue,
    handleDeskClicked,
    getAvailableDesks,
    confirmBookDesk,
    bookDesks,
    setNotifyUser,
    setConfirmOpen,
    setSelectedBookingOwnerId,
    handleWeekDaysChange
  } = useBookingForm()

  const availableDesks = getAvailableDesks()

  // Calculate the date range for filtering bookings
  const dateRange = React.useMemo(() => {
    return generateDateRange(
      new Date(userState.selectedFromDate),
      userState.selectedRecurUnit,
      userState.selectedRecurAmount,
      userState.selectedWeekDays
    )
  }, [
    userState.selectedFromDate,
    userState.selectedRecurUnit,
    userState.selectedRecurAmount,
    userState.selectedWeekDays
  ])

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const hasDrawingData = !!userState.selectedOffice?.drawingData

  return (
    <Grid container spacing={10}>
      <Grid item xs={12} lg={12}>
        <BookingForm
          selectedFromDate={userState.selectedFromDate}
          selectedRecurAmount={userState.selectedRecurAmount}
          selectedRecurUnit={userState.selectedRecurUnit}
          selectedWeekDays={userState.selectedWeekDays}
          selectedOffice={userState.selectedOffice}
          offices={userState.offices}
          busy={busy}
          notifyUser={notifyUser}
          onFromDateChange={handleFromDayChange}
          onAmountChange={setNewAmountValue}
          onUnitChange={handleUnitChange}
          onWeekDaysChange={handleWeekDaysChange}
          onOfficeChange={handleOfficeChange}
          onNotificationClose={() => setNotifyUser('')}
        />

        {/* Team Member Selector */}
        {userState.user && (
          <TeamMemberSelector
            selectedUserId={selectedBookingOwnerId}
            onUserChange={setSelectedBookingOwnerId}
            currentUser={userState.user}
            teamMembers={teamMembers}
          />
        )}

        {/* Tabs for Desks and Office Layout */}
        {userState.officeDesks && userState.officeDesks.length > 0 && (
          <Box sx={{ width: '100%', mt: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label='Desk booking tabs'
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label='Desks' id='tab-0' aria-controls='tabpanel-0' />
              {hasDrawingData && (
                <Tab
                  label='Office Layout'
                  id='tab-1'
                  aria-controls='tabpanel-1'
                />
              )}
            </Tabs>

            {/* Desks Tab */}
            <Box
              role='tabpanel'
              hidden={activeTab !== 0}
              id='tabpanel-0'
              aria-labelledby='tab-0'
              sx={{ pt: 3 }}
            >
              {activeTab === 0 && (
                <DeskAvailability
                  desks={availableDesks}
                  selectedDesks={selectedDesks}
                  uid={userState.user?.uid}
                  onDeskClicked={handleDeskClicked}
                  officeName={userState.selectedOffice?.name}
                  busy={busy}
                  onBookSelected={confirmBookDesk}
                  bookings={userState.bookings}
                  dateRange={dateRange}
                />
              )}
            </Box>

            {/* Office Layout Tab */}
            {hasDrawingData && (
              <Box
                role='tabpanel'
                hidden={activeTab !== 1}
                id='tabpanel-1'
                aria-labelledby='tab-1'
                sx={{ pt: 3 }}
              >
                {activeTab === 1 && (
                  <OfficeDrawingViewer
                    drawingData={userState.selectedOffice!.drawingData!}
                    officeName={userState.selectedOffice!.name || 'Office'}
                  />
                )}
              </Box>
            )}
          </Box>
        )}

        <DialogAlert
          open={confirmOpen}
          confirmLabel='Yes'
          cancelLabel='No'
          text={
            userState.selectedRecurUnit === 'Once off'
              ? `Are you sure you want to book ${selectedDesks[0]?.name} for ${
                  userState.selectedRecurAmount
                } consecutive day${
                  userState.selectedRecurAmount > 1 ? 's' : ''
                }?`
              : `Are you sure you want to book ${
                  selectedDesks[0]?.name
                } for the selected days every week for the next 3 months (approximately ${
                  userState.selectedWeekDays.length * 13
                } bookings)?`
          }
          title='Confirm Booking'
          onConfirm={() => bookDesks(selectedBookingOwnerId)}
          onClose={() => setConfirmOpen(false)}
        />
      </Grid>
    </Grid>
  )
}
